import React from "react";
import { FileText, Briefcase, Package, Car } from "lucide-react";

const docPrep = [
  { name: "Court Bundle preparation", price: "£280" },
  { name: "Statements & position statements", price: "£85 / hour" },
];

const hearing = [
  { name: "Half day attendance (up to 4 hours)", price: "£280" },
  { name: "Full day attendance", price: "£420" },
  { name: "Additional hours", price: "£85 / hour" },
];

const packages = [
  {
    name: "Basic Package",
    detail: "Court Bundle + Half day attendance",
    price: "£450",
  },
  {
    name: "Full Support Package",
    detail: "Court Bundle + Full day attendance",
    price: "£680",
    featured: true,
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32"
    >
      <div className="absolute inset-0 grain-overlay opacity-30" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#C89D54]" />
            <span className="overline">Services & Pricing</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#F4F0EB] tracking-tight leading-[1.05]">
            Transparent pricing.
            <span className="block italic text-[#C89D54]">No surprises.</span>
          </h2>
          <p className="mt-6 font-sans-body text-[#A6A098] text-base leading-relaxed max-w-2xl">
            Clear, fixed fees for documents and court attendance. Choose
            individual services or a complete support package — whichever fits
            your case.
          </p>
        </div>

        {/* Individual Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-14">
          <ServiceCard
            testId="service-doc-prep"
            Icon={FileText}
            title="Preparation of Documents"
            items={docPrep}
          />
          <ServiceCard
            testId="service-hearing"
            Icon={Briefcase}
            title="Court Hearing Support"
            items={hearing}
          />
        </div>

        {/* Packages */}
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-8">
            <Package size={18} className="text-[#C89D54]" />
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#F4F0EB]">
              Package Deals
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {packages.map((p, i) => (
              <div
                key={p.name}
                data-testid={`package-card-${i}`}
                className={`relative p-8 md:p-10 transition-all duration-500 hover:-translate-y-1 ${
                  p.featured
                    ? "bg-gradient-to-b from-[#C89D54]/8 to-transparent border border-[#C89D54]/50"
                    : "bg-[#262321] border border-[#C89D54]/20"
                }`}
              >
                {p.featured && (
                  <div className="absolute -top-3 left-8 px-3 py-1 bg-[#C89D54] text-[#1A1918] text-xs uppercase tracking-widest font-semibold">
                    Recommended
                  </div>
                )}
                <h4 className="font-serif-display text-2xl md:text-3xl text-[#F4F0EB]">
                  {p.name}
                </h4>
                <p className="mt-2 text-sm text-[#A6A098]">{p.detail}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-serif-display text-5xl md:text-6xl text-[#C89D54]">
                    {p.price}
                  </span>
                </div>
                <button
                  data-testid={`package-cta-${i}`}
                  onClick={() =>
                    document
                      .getElementById("contact")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="mt-8 text-sm font-semibold tracking-widest uppercase text-[#C89D54] hover:text-[#DDB46F] border-b border-[#C89D54]/40 pb-1 transition-colors"
                >
                  Enquire about this package →
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Travel note */}
        <div
          data-testid="travel-note"
          className="mt-12 flex items-center gap-4 border border-[#C89D54]/20 px-6 py-5 bg-[#262321]"
        >
          <Car size={20} className="text-[#C89D54] shrink-0" />
          <p className="text-sm text-[#A6A098]">
            Travel charged at{" "}
            <span className="text-[#F4F0EB] font-semibold">£0.45 per mile</span>{" "}
            outside the Mansfield area.
          </p>
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ testId, Icon, title, items }) => (
  <div
    data-testid={testId}
    className="bg-[#262321] border border-[#C89D54]/20 p-8 md:p-10 hover:border-[#C89D54]/50 hover:-translate-y-1 transition-all duration-500"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="h-10 w-10 flex items-center justify-center border border-[#C89D54]/30">
        <Icon size={18} className="text-[#C89D54]" />
      </div>
      <h3 className="font-serif-display text-2xl md:text-3xl text-[#F4F0EB]">
        {title}
      </h3>
    </div>
    <ul className="space-y-4">
      {items.map((item) => (
        <li
          key={item.name}
          className="flex items-baseline justify-between gap-6 border-b border-[#C89D54]/10 pb-3 last:border-b-0 last:pb-0"
        >
          <span className="text-[#F4F0EB] text-[15px]">{item.name}</span>
          <span className="font-serif-display text-xl text-[#C89D54] whitespace-nowrap">
            {item.price}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export default Services;
